import {
  BackWrapper,
  Container,
  ContainerWrapper,
  NoContentLabel,
  StickyTop,
  Title,
  TitleWrapper,
} from './BomPage.styled';
import { useDispatch, useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { useTranslation } from 'react-i18next';
import BackButton from 'components/BackButton/BackButton';
import { setIncludeBomItems } from 'store/Form/actions/form';
import { useEffect, useRef, useState } from 'react';
import { SortDirection } from 'components/Table/types';
import Table from 'components/Table/Table';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { BomItemCard } from './BomItemCard/BomItemCard';
import { columnKeyMap, tableConfig } from './constants';
import { formatBomItems, IFormattedBOMItem, sortBomItems } from './helpers';
import TableColumnSelector from './TableColumnSelector/TableColumnSelector';
import { savePageFilters } from 'store/Filters/actions/filters';
import { FiltersPageEnum } from 'store/Filters/constants';
import { useNavigateBackFromConfiguratorSubPage } from 'utils/hooks/useNavigateBackFromConfiguratorSubPage';
import { useScrollToTopOnMount } from 'utils/hooks/useScrollToTopOnMount';
import { useElementHeight } from 'utils/hooks/useElementHeight';

const BomPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const navigateBack = useNavigateBackFromConfiguratorSubPage();

  // Opened from the configurator, which keeps the window offset across the
  // navigation - this page is read from the top.
  useScrollToTopOnMount();

  const bomItems =
    useSelector((state: IRootReducerState) => state.modelInfo.bomItems) || [];

  useEffect(() => {
    return () => {
      dispatch(setIncludeBomItems(false));
    };
  }, []);

  const visibleColumns = useSelector(
    (state: IRootReducerState) =>
      state.filtersInfo?.bomPage?.visibleColumns ??
      tableConfig.rowConfigs.map((col) => col.header)
  );

  useEffect(() => {
    if (!visibleColumns || visibleColumns.length === 0) {
      dispatch(
        savePageFilters({
          page: FiltersPageEnum.BOM,
          data: {
            visibleColumns: tableConfig.rowConfigs.map((col) => col.header),
          },
        })
      );
    }
  }, [dispatch, visibleColumns]);

  const queryRequestStatus = useSelector(
    (state: IRootReducerState) => state.modelInfo.queryStatus
  );

  const [sortBy, setSortBy] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<SortDirection | undefined>(
    SortDirection.NONE
  );

  const visibleRowConfigs = tableConfig.rowConfigs.filter((col) =>
    visibleColumns?.includes(col.header)
  );

  const formattedBomItems: IFormattedBOMItem[] = sortBomItems(
    formatBomItems(bomItems) || [],
    sortBy,
    sortDirection,
    visibleRowConfigs
  );

  const filteredTableData = formattedBomItems.map((item) =>
    Object.fromEntries(
      Object.entries(item).filter(([key]) =>
        visibleColumns?.includes(
          Object.keys(columnKeyMap).find((col) => columnKeyMap[col] === key) ||
            ''
        )
      )
    )
  );

  const { isSmallTablet } = useBreakpointFlag();

  const stickyTopRef = useRef<HTMLDivElement>(null);
  const stickyTopHeight = useElementHeight(stickyTopRef);

  return (
    <ContainerWrapper>
      <StickyTop ref={stickyTopRef}>
        <BackWrapper>
          <BackButton
            destination={t('Configuration form')}
            onClick={navigateBack}
          />
        </BackWrapper>
        <TitleWrapper>
          <Title>{t('Bill of materials')}</Title>

          {!isSmallTablet && (
            <TableColumnSelector
              columns={tableConfig.rowConfigs}
              visibleColumns={visibleColumns}
            />
          )}
        </TitleWrapper>
      </StickyTop>
      <Container>
        {!queryRequestStatus?.attempt && !formattedBomItems.length ? (
          <NoContentLabel>
            {t('There are currently no BOM items')}
          </NoContentLabel>
        ) : isSmallTablet ? (
          formattedBomItems.map(
            (formattedBomItem: IFormattedBOMItem, index: number) => (
              <BomItemCard
                bomItem={formattedBomItem}
                isLast={index === formattedBomItems.length - 1}
                key={`bom-item-card-${index}`}
              />
            )
          )
        ) : (
          <Table
            // Every model query returns BOM items, so they are usually already
            // here while the page's own query runs - only show the skeleton
            // when there is nothing to show yet.
            isLoading={queryRequestStatus?.attempt && !formattedBomItems.length}
            page={1}
            total={filteredTableData.length}
            hasExtraAction={false}
            loadPage={() => {
              //
            }}
            perPage={10}
            tableData={filteredTableData}
            tableConfig={{ rowConfigs: visibleRowConfigs }}
            sortParams={{ sortBy, setSortBy, sortDirection, setSortDirection }}
            withoutPagination={true}
            isStickyHeader={true}
            stickyHeaderOffset={stickyTopHeight}
            allowTextSelection={true}
          />
        )}
      </Container>
    </ContainerWrapper>
  );
};

export default BomPage;
